import { 
    Typography, 
    styled as muiStyled,
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    AccordionSummary as MuiAccordionSummary,
    IconButton
} from '@mui/material';
import { ArrowRight, Cancel as CancelIcon } from '@mui/icons-material';
import GradeDisplay from 'components/GradeDisplay';
import { find, get, map } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { revokeTrafficModal } from "store/wheel/actions";
import { useTraffic } from "store/wheel/selectors";
import AccordinButton from "./AccordinButton";
import ResizeAndDragabble from "./ResizeAndDraggable";
import { isGradedByTeacher } from "utils/methods/math";
import styled from 'styled-components';
import ClickOutside from "./OnClickOutside";

// Styled-components for layout and basic elements
const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 50%;
    position: relative;
    padding: 0 20px;
    z-index: 99999999;
    position: fixed;
    cursor: default;
    transform: translate(31%, 0%);
    @media (max-width: 1450px) {
        transform: translate(0%, 0%);
    }
    @media (max-width: 992px) {
        transform: translate(0%, 0%);
    }
    @media (max-width: 768px) {
        width: 100%;
        left: 15px;
        top: 15px;
        transform: none;
        padding: 0;
        overscroll-behavior: contain;
    }
`;

const AccordianTitle = styled(Typography)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
`;

const Rotate = styled.div`
    align-items: center;
    display: flex;
    height: 16px;
    justify-content: center;
    margin-right: 4px;
    transform: ${props => props.open ? 'rotate(90deg)' : 'rotate(0deg)'};
    transition: all 300ms ease;
    width: 16px;
`;

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    min-height: 0;
    height: calc(85%);
    background-color: white;
    padding-left: 10px;
    padding-right: 10px;
    max-height: 100%;
    overflow: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
    cursor: default;
`;

const MainWrapper = styled.div`
    width: 100%;
`;

const FeedbackDragHandle = styled.div`
    cursor: move;
    user-select: none;
    &:active {
        cursor: move;
    }
    & .no-drag,
    & .no-drag * {
        cursor: pointer;
    }
`;

// MUI styled components
const Accordion = muiStyled(MuiAccordion)(({ theme }) => ({
    border: '1px solid white',
    boxShadow: 'none',
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
    '&.Mui-expanded': {
        margin: 'auto',
    }
}));

const AccordionSummary = muiStyled(MuiAccordionSummary)(({ theme }) => ({
    backgroundColor: '#03989e',
    color: 'white',
    textTransform: 'capitalize',
    borderBottom: '1px solid white',
    marginBottom: -1,
    minHeight: 30,
    height: 30,
    '&.Mui-expanded': {
        minHeight: 30,
    },
    '& .MuiAccordionSummary-content': {
        '&.Mui-expanded': {
            margin: '8px 0',
        }
    }
}));

const AccordionDetails = muiStyled(MuiAccordionDetails)(({ theme }) => ({
    padding: '2px',
    display: "flex",
    flexDirection: "column"
}));

const StyledCancelIcon = muiStyled(IconButton)(({ theme }) => ({
    position: 'absolute',
    right: 7,
    top: 14,
    padding: 0,
    '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: 'red',
        backgroundColor: 'white',
        borderRadius: '50%'
    }
}));

const PreviousFDWidgets = ({ currentClass, sharableWidget }) => {
  const filterShareImage = find(
    sharableWidget,
    (obj) => obj?.widget_type === "image"
  );
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const [trafficModal] = useTraffic();
  const [showAccordin, setShowAccordin] = useState(true);
  const trafficData = useMemo(() => trafficModal?.widget_menu ?? [], [trafficModal]);
  const filterByWidget = useMemo(() => find(trafficData, (item) => item?.menu === "previous_Feedback"), [trafficData]);
  const currentSubmission = useMemo(() => filterByWidget?.currentSubmission ?? {}, [filterByWidget]);
  const data = useMemo(() => filterByWidget?.data ?? [], [filterByWidget]);
  const pastSubmissions = useMemo(() => {
    if (!Array.isArray(filterByWidget?.pas_submission)) return [];
    const resp = [...filterByWidget.pas_submission].sort((a, b) => {
      return parseInt(a.submissions) - parseInt(b.submissions);
    });
    return resp;
  }, [filterByWidget]);
  const [expanded, setExpanded] = useState(null);
  const [widgetDimensions, setWidgetDimensions] = useState({
    x: 150,
    y: 205,
    width: 400,
    height: 600,
  });
  const [minWidth, setMinWidth] = useState(340);
  const [isMobile, setIsMobile] = useState(false);


  useEffect(() => {
    if (pastSubmissions && pastSubmissions.length > 0) {
      //const last_sub = [...pastSubmissions].sort((a, b) => parseInt(b.submissions) - parseInt(a.submissions))[0];
      setExpanded(pastSubmissions[pastSubmissions.length - 1]?.id);
    }
  }, [pastSubmissions]);

  const isFeedbackWidgetMounted = useMemo(
    () =>
      Boolean(
        (filterByWidget?.view && filterByWidget?.menu === 'previous_Feedback') ||
          filterShareImage?.id,
      ),
    [filterByWidget?.view, filterByWidget?.menu, filterShareImage?.id],
  );

  useEffect(() => {
    const shouldLock = isMobile && isFeedbackWidgetMounted && showAccordin;
    if (!shouldLock) return undefined;
    const scrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isMobile, isFeedbackWidgetMounted, showAccordin]);

  useEffect(() => {
    const calculateDimensions = () => {
      const mobileCheck = window.innerWidth < 768;
      setIsMobile(mobileCheck);
      if (mobileCheck) {
        const mobileWidth = Math.floor(window.innerWidth * 0.8);
        const mobileHeight = Math.floor((mobileWidth * 600) / 400);
        const centerX = Math.floor((window.innerWidth - mobileWidth) / 2);
        const centerY = Math.floor((window.innerHeight - mobileHeight) / 2);
        setWidgetDimensions({
          x: centerX,
          y: centerY,
          width: mobileWidth,
          height: mobileHeight,
        });
        setMinWidth(Math.max(280, Math.floor(window.innerWidth * 0.7)));
      } else {
        setWidgetDimensions({
          x: 150,
          y: 205,
          width: 400,
          height: 600,
        });
        setMinWidth(340);
      }
    };

    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, []);

  const onClose = async () => {
    await dispatch(revokeTrafficModal(filterByWidget?.id));
  };

  const handleTouch = (event) => {
    event.preventDefault();
    onClose();
  };

  const handleChangeExpend = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : null);
  };

  const pastSubmissionView = useCallback(() => {
    const questions = get(currentSubmission, "assignment.questions", []);
    const displayName = get(currentSubmission, "displayName", get(data, "displayName"));
    
    if (pastSubmissions && pastSubmissions.length > 0) {
      return (
        <MainWrapper className="no-drag">
          {map(pastSubmissions, (pt, index) => {
            const { id, answers, feedback, ai_feedback, ai_suggestion, audioFeedback, grade, islate, submissions } = pt || {};
            const is_returned =  (pt?.returned > 0 && pt?.submissions === (pt?.returned - 1));
            return (
              <Accordion 
                key={id}
                expanded={expanded === id} 
                onChange={handleChangeExpend(id)}
                elevation={0}
              >
                <AccordionSummary>
                  <AccordianTitle variant="body1">
                    <span>{"Submission-" + (index + 1)} {is_returned ? `(Returned)` : ""} {pt.is_current ? " (Current Submission)" : ""} {isGradedByTeacher(pt, true)}</span>
                    <Rotate open={expanded === id}>
                      <ArrowRight />
                    </Rotate>
                  </AccordianTitle>
                </AccordionSummary>
                <AccordionDetails>
                  <div>
                    <GradeDisplay
                      key={id}
                      answers={answers}
                      feedback={feedback}
                      ai_feedback={ai_feedback}
                      ai_suggestion={ai_suggestion}
                      audioFeedback={audioFeedback}
                      grade={grade}
                      name={displayName}
                      questions={questions}
                      type={"past due"}
                      isLate={islate}
                      isExpended
                      assignment={pt}
                      hasFeedback={Boolean(feedback || audioFeedback)}
                      isFeedBack
                      activity={data}
                    />
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </MainWrapper>
      );
    }
    return null;
  }, [data, currentSubmission, pastSubmissions, expanded]);

  return (
    <div style={{ position: "relative", zIndex: 1000 }}>
      {((filterByWidget?.view && filterByWidget?.menu === "previous_Feedback") ||
        filterShareImage?.id) && (
        <Container className="allCheckListSEction wigetsContainer">
          <div className="mainCheckList h-full">
          <ClickOutside onClickOutside={() => setIsDragging(false)}>
            <ResizeAndDragabble
              key={`widget-${isMobile ? 'mobile' : 'desktop'}-${widgetDimensions.width}`}
              defaultValue={widgetDimensions}
              onFocusValue={isDragging}
              minWidth={minWidth}
              minHeight={355}
              showAccordin={showAccordin}
              aspectRatio
              center={true}
              dragHandleClassName="previous-feedback-drag-handle"
            >
              <div
                className={`checkListTopSection ${isDragging && showAccordin ? "widgetMobAndTab WdgtBorderCstm" : ""}`}
                style={{
                  position: "relative",
                  boxShadow: "rgba(0, 0, 0, 0.2) 0px 2px 4px",
                  height: showAccordin ? "100%" : "0px",
                  width: "100%",
                  padding: "0px",
                  ...(showAccordin
                    ? {
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }
                    : {}),
                }}
              >
                <StyledCancelIcon
                  className="no-drag"
                  onClick={onClose}
                  onTouchStart={handleTouch}
                  size="small"
                >
                  <CancelIcon />
                </StyledCancelIcon>

                <FeedbackDragHandle
                  className={`checkListFirstTxt previous-feedback-drag-handle bg-blue-10 h-44px pt-17px pb-14px widgetFooterGradientOrange ${
                    showAccordin ? "br-topLeft-topRight-20px" : "br-20px"
                  }`}
                >
                  <AccordinButton
                    open={showAccordin}
                    onClose={() => setShowAccordin(prev => !prev)}
                    shareableWidget={sharableWidget}
                    isDragging={isDragging}
                  />
                  <Typography
                    variant="body2"
                    component="span"
                    className="text-white text-ft12-700"
                  >
                    Submission Feedback
                  </Typography>
                </FeedbackDragHandle>
                {showAccordin && (
                  <Wrap className="prevFeedBackWdgt no-drag">
                    {pastSubmissionView()}
                  </Wrap>
                )}
                {showAccordin && (
                  <div className="widgetFooter widgetFooterGradientOrange no-drag"><label>Widget</label></div>
                )}
              </div>
            </ResizeAndDragabble>
          </ClickOutside>
          </div>
        </Container>
      )}
    </div>
  );
};

export default PreviousFDWidgets;
